import { documentDisplayNameInText } from '@/utils/documentDisplayName'
/**
 * MYAC-1302: the long em dash (U+2014) reads as machine-written text to the
 * people MAC writes for. Our own copy never uses it (guarded by
 * `locales/typography.test.ts` and `utils/noEmDashInCode.test.ts`), and model
 * output has it replaced at render time, because a prompt instruction cannot
 * guarantee it and a render step can. A plain hyphen is fine.
 */
export const EM_DASH = '—'

const EM_DASH_RE = /—/g
const SPACE_RE = /\s/

/**
 * Replace every em dash with a plain hyphen, spaced like a clause break:
 * "a — b" and "a—b" both read "a - b", "— source" reads "- source", and a
 * value that is only the dash reads "-". Existing whitespace is kept as it is,
 * so a fragment that starts or ends at a markdown token boundary keeps its
 * spacing.
 *
 * Mechanical on purpose: a render step cannot read the sentence to pick a
 * comma or a colon, and a spaced hyphen never changes what the sentence says.
 */
export function stripEmDashes(text: string): string
export function stripEmDashes(text: string | null | undefined): string | null | undefined
export function stripEmDashes(text: string | null | undefined): string | null | undefined {
  if (!text || !text.includes(EM_DASH)) return text
  return text.replace(EM_DASH_RE, (_match: string, offset: number, whole: string) => {
    const prev = whole[offset - 1]
    const next = whole[offset + 1]
    const before = prev !== undefined && !SPACE_RE.test(prev) ? ' ' : ''
    const after = next !== undefined && !SPACE_RE.test(next) ? ' ' : ''
    return `${before}-${after}`
  })
}

/**
 * Model prose, ready to show: em dashes flattened (the house rule) and the
 * ingestion's conversion suffix dropped from any file name inside the sentence
 * (MYAC-1343). Every surface that prints a worker's sentence calls this instead
 * of `stripEmDashes` alone; `verbatim` surfaces call neither.
 */
export function displayProse(text: string | null | undefined): string {
  return documentDisplayNameInText(stripEmDashes(text ?? ''))
}
